/**
 * Small display formatters shared by the HUD and the menu screens.
 *
 * These live here because every previous copy had drifted or was wrong:
 * `adminScreen` had a correct duration formatter that `profileScreen` did not
 * use (so 4 hours of playtime rendered as "240m"), and the HUD's ordinals were
 * a 4-entry lookup with a `${n}th` fallback that produced "21th".
 */

/** Compact duration from SECONDS: `45s`, `7m 12s`, `4h 3m`. */
export function fmtDur(s: number): string {
  const t = Math.max(0, Math.floor(s));
  if (t < 60) return `${t}s`;
  if (t < 3600) return `${Math.floor(t / 60)}m ${t % 60}s`;
  return `${Math.floor(t / 3600)}h ${Math.floor((t % 3600) / 60)}m`;
}

/** English ordinal: 1st, 2nd, 3rd, 4th … 11th, 21st, 22nd, 23rd, 111th.
 *  The teens are the exception the naive `n % 10` rule gets wrong. */
export function ordinal(n: number): string {
  const v = Math.floor(n);
  const teen = Math.abs(v) % 100;
  if (teen >= 11 && teen <= 13) return `${v}th`;
  switch (Math.abs(v) % 10) {
    case 1: return `${v}st`;
    case 2: return `${v}nd`;
    case 3: return `${v}rd`;
    default: return `${v}th`;
  }
}

/** Compact large count for stat tiles: 9500 → "9500", 12400 → "12.4k",
 *  320000 → "320k", 3200000 → "3.2M". Lifetime damage crosses 100k within
 *  hours of play; a 7-digit number does not fit a stat tile. */
export function fmtCompact(n: number): string {
  const v = Math.max(0, Math.floor(n));
  if (v < 10000) return String(v);
  if (v < 100000) return `${(v / 1000).toFixed(1)}k`;
  if (v < 1000000) return `${Math.round(v / 1000)}k`;
  return `${(v / 1000000).toFixed(1)}M`;
}

/** Stopwatch time from MILLISECONDS with tenths: `42.7s`, `1:23.4`. Used by
 *  the practice LADDER SPRINT card/records, where whole seconds are too
 *  coarse to separate two good runs. */
export function fmtStopwatch(ms: number): string {
  // Round to integer TENTHS first, then format from that one number: floating
  // through seconds made 59.96s print "60.0s" (t<60 picked the branch, then
  // toFixed rounded past it) and `(t % 1) * 10` truncated one tenth low on 40%
  // of exact-100ms server times (60.3 % 1 = 0.2999…).
  const tenths = Math.round(Math.max(0, ms) / 100);
  if (tenths < 600) return `${(tenths / 10).toFixed(1)}s`;
  const m = Math.floor(tenths / 600);
  const rem = tenths - m * 600;
  return `${m}:${String(Math.floor(rem / 10)).padStart(2, "0")}.${rem % 10}`;
}

/** Countdown clock from MILLISECONDS: `04:30`, and `1:00:00` past an hour.
 *  Rooms allow round times up to 3600 s, which the old mm:ss-only formatter
 *  rendered as "60:00". */
export function fmtClock(msLeft: number): string {
  const s = Math.max(0, Math.ceil(msLeft / 1000));
  const mm = String(Math.floor(s / 60) % 60).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  const h = Math.floor(s / 3600);
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}
